import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@mdvip/shared/components/ui/accordion';
import { Checkbox } from '@mdvip/shared/components/ui/checkbox';
import { Label } from '@mdvip/shared/components/ui/label';
import { cn } from '@mdvip/shared/lib/utils';
import type { ReactNode } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

export interface FacetOption {
  value: string;
  label: ReactNode;
  /** Matching-item count shown on the right of the option. An option with count 0 renders disabled unless already selected. */
  count?: number;
}

/** One facet — a named group of values (e.g. a taxonomy category). */
export interface Facet {
  id: string;
  label: ReactNode;
  options: FacetOption[];
}

/** Selected values, keyed by facet id. */
export type FacetValue = Record<string, string[]>;

function toggle(list: string[] | undefined, v: string): string[] {
  const next = (list ?? []).slice();
  const i = next.indexOf(v);
  if (i > -1) next.splice(i, 1);
  else next.push(v);
  return next;
}

export function countSelected(value: FacetValue): number {
  return Object.keys(value ?? {}).reduce((n, k) => n + (value[k]?.length ?? 0), 0);
}

/**
 * CFGroup — one facet as an open checkbox group, shared between `ContentFilters`
 * (the rail layout) and `ContentFiltersDrawer`. Shows `limit` options, then the
 * rest behind a "Show all N" / "Show fewer" toggle. Holds the expander's own
 * state, which is why it is a component rather than a render helper.
 */
export function CFGroup({
  facet,
  selected = [],
  onChange,
  limit = 6,
  hideLegend,
}: {
  facet: Facet;
  selected?: string[];
  onChange?: (facetId: string, values: string[]) => void;
  limit?: number;
  hideLegend?: boolean;
}) {
  const { t } = useTranslation('shared');
  const [all, setAll] = useState(false);
  const opts = all ? facet.options : facet.options.slice(0, limit);
  const rest = facet.options.length - opts.length;
  const instanceId = useId();
  const unavailableId = `cfg-${instanceId}-${facet.id}-unavailable`;
  const hasUnavailable = opts.some((o) => o.count === 0 && !selected.includes(o.value));

  return (
    <fieldset className="flex flex-col gap-2">
      {/* Hidden rather than dropped inside an accordion: the trigger names the
          group on screen, and the name stays for a screen reader. */}
      <legend className={cn('pb-1 text-sm font-semibold text-foreground', hideLegend && 'sr-only')}>
        {facet.label}
        {selected.length ? t('contentFilters.selectedCount', { count: selected.length }) : ''}
      </legend>
      <div className="flex flex-col gap-1">
        {opts.map((o) => {
          const id = `cfg-${instanceId}-${facet.id}-${o.value}`;
          const on = selected.includes(o.value);
          const unavailable = o.count === 0 && !on;
          return (
            <div key={o.value} className="flex min-h-11 items-center gap-3">
              <Checkbox
                id={id}
                checked={on}
                disabled={unavailable}
                aria-describedby={unavailable ? unavailableId : undefined}
                onCheckedChange={() => onChange?.(facet.id, toggle(selected, o.value))}
              />
              <Label
                htmlFor={id}
                className={cn(
                  'flex flex-1 items-center justify-between gap-4 text-sm font-normal',
                  unavailable && 'text-muted-foreground',
                )}
              >
                {o.label}
                <span className="text-xs tabular-nums text-muted-foreground">
                  {o.count !== undefined ? o.count : ''}
                </span>
              </Label>
            </div>
          );
        })}
      </div>
      {rest > 0 || all ? (
        <button
          type="button"
          onClick={() => setAll(!all)}
          className={cn(
            'inline-flex min-h-6 items-center gap-2 self-start text-sm font-semibold text-foreground hover:text-primary',
            FOCUS,
          )}
        >
          <span className="underline decoration-from-font underline-offset-2">
            {all ? t('contentFilters.showFewer') : t('contentFilters.showAll', { count: facet.options.length })}
          </span>
          <Icon name={all ? 'chevronUp' : 'chevronDown'} size="xs" />
        </button>
      ) : null}
      {hasUnavailable ? (
        <span id={unavailableId} className="sr-only">
          {t('contentFilters.optionUnavailable')}
        </span>
      ) : null}
    </fieldset>
  );
}

export interface ContentFiltersProps {
  facets?: Facet[];
  value?: FacetValue;
  onChange?: (facetId: string, values: string[]) => void;
  /**
   * A caller's own filter that isn't a taxonomy facet (e.g. a saved-only checkbox) — leads the
   * rail, between the heading and the accordions, per the design (`features/ContentListing.jsx`'s
   * rail layout: "The caller's own control leads the rail... above the facet accordions rather
   * than under them").
   */
  primaryControls?: ReactNode;
  className?: string;
}

/**
 * ContentFilters — the standing filter rail: every facet as an independently
 * expandable accordion section, real-time (checking an option narrows results
 * immediately, no Apply button). A facet already carrying a selection starts
 * open; otherwise the first facet does.
 *
 * Never given a "Clear all" of its own — every real usage in the design system
 * (the compound's own reference demo and the Recipes/Nutrition Articles pages)
 * puts the page's one "Clear all" in a sibling active-filter chip row instead,
 * so this component doesn't carry that capability.
 */
export function ContentFilters({ facets = [], value = {}, onChange, primaryControls, className }: ContentFiltersProps) {
  const { t } = useTranslation('shared');
  const total = countSelected(value);
  const railOpen = facets.filter((f) => (value[f.id] ?? []).length).map((f) => f.id);
  // Accordion's defaultValue is uncontrolled, so this "starts open" set is fixed at mount —
  // facets arriving after the initial render (e.g. a later query response) won't reopen it.
  const defaultOpen = railOpen.length ? railOpen : facets.slice(0, 1).map((f) => f.id);

  return (
    // biome-ignore lint/a11y/useSemanticElements: groups a heading + accordion buttons, not form controls
    <div
      data-slot="content-filters"
      role="group"
      aria-label={t('contentFilters.heading')}
      className={cn('flex w-full flex-col gap-3 rounded-3xl bg-card p-6 shadow-sm', className)}
    >
      <div className="border-b pb-2">
        <h3 className="text-sm font-semibold text-foreground">
          {t('contentFilters.heading')}
          {total ? t('contentFilters.selectedCount', { count: total }) : ''}
        </h3>
      </div>
      {primaryControls && <div className="flex flex-wrap items-center gap-3 border-b pb-3">{primaryControls}</div>}
      <Accordion multiple defaultValue={defaultOpen}>
        {facets.map((f) => {
          const selected = value[f.id] ?? [];
          return (
            <AccordionItem key={f.id} value={f.id} className="border-b last:border-b-0">
              <AccordionTrigger className="py-3 font-semibold">
                <span>
                  {f.label}
                  {selected.length ? t('contentFilters.selectedCount', { count: selected.length }) : ''}
                </span>
              </AccordionTrigger>
              <AccordionContent className="pb-3">
                <CFGroup facet={f} selected={selected} onChange={onChange} hideLegend />
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}
